import React, { useEffect, useState } from 'react';
import { AttachmentDto, CreateEmailDto } from '../CreateEmailDto';
import { EmailFooter } from './EmailFooter';
import { EmailRichTextEditor } from './EmailRichTextEditor';
import { useForm } from 'react-hook-form';
import { EmailHeader } from './EmailHeader';
import { ApplicationService } from '../../../../../services/applicant_tracking/ApplicationService';
import { SelectOption } from '../../../../../components/Select';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { Email } from '../../../../../entities/Email';
import { User } from '../../../../../entities/User';
import { EmailTemplate } from '../../../../../entities/EmailTemplate';
import { EmailTemplateService } from '../../../../../services/EmailTemplateService';
import { UserSelectionService } from '../../../../../services/UserSelectionService';

interface PropTypes {
  applicationIds: number[];
  toEmails: string[];
  replyToEmail?: Email;
  onClose: (succeeded?: boolean) => void;
}

function getInitEmail(to: string): CreateEmailDto {
  return {
    to: to,
    cc: [],
    bcc: [],
    subject: null,
    editorState: null,
    editorStateHtml: null,
    attachments: [],
    replyToEmailId: null,
  };
}

function sendEmail(
  singleEmailRecipient: boolean,
  applicationIds: number[],
  replyToEmail: Email,
  data: CreateEmailDto,
  cc: SelectOption[],
  bcc: SelectOption[],
  subject: string,
  html: string,
  attachments: AttachmentDto[],
) {
  return singleEmailRecipient
    ? ApplicationService.sendEmail({
        applicationId: applicationIds[0],
        to: data.to,
        cc: cc?.map((option) => option.value),
        bcc: bcc?.map((option) => option.value),
        subject: subject,
        bodyHtml: html,
        attachments: attachments,
        replyToEmailId: replyToEmail?.id,
      })
    : ApplicationService.sendBulkEmails({
        applicationIds: applicationIds,
        to: data.to,
        cc: cc?.map((option) => option.value),
        bcc: bcc?.map((option) => option.value),
        subject: subject,
        bodyHtml: html,
        attachments: attachments,
        replyToEmailId: replyToEmail?.id,
      });
}

export function EmailModalBody(props: PropTypes) {
  const { register, handleSubmit, formState, control } =
    useForm<CreateEmailDto>({
      mode: 'onChange',
      defaultValues: getInitEmail(props.toEmails.join(', ')),
    });

  const [attachments, setAttachments] = useState<AttachmentDto[]>(null);
  const [cc, setCc] = useState<SelectOption[]>(null);
  const [bcc, setBcc] = useState<SelectOption[]>(null);
  const [html, setHtml] = useState<string>(null);
  const [subject, setSubjectText] = useState<string>(
    props.replyToEmail?.subject,
  );
  const [isLoading, setIsLoading] = useState<boolean>(null);
  const [users, setUsers] = useState<User[]>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(null);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>(null);
  const singleEmailRecipient = props.toEmails.length === 1;

  async function handleEmailTemplate(emailTemplate: EmailTemplate) {
    const template = singleEmailRecipient
      ? await EmailTemplateService.replaceVariables(
          emailTemplate.id,
          props.applicationIds[0],
        )
      : emailTemplate;

    setEmailTemplate(template);
  }

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (users == null) {
        setUsers(await UserSelectionService.list());
      }
      if (emailTemplates == null) {
        setEmailTemplates(await EmailTemplateService.list());
      }
      setIsLoading(false);
    })();
  }, [users]);

  const processAndSendEmail = async (
    data: CreateEmailDto,
    _event: React.SyntheticEvent<any, SubmitEvent>,
  ) => {
    setIsLoading(true);
    sendEmail(
      singleEmailRecipient,
      props.applicationIds,
      props.replyToEmail,
      data,
      cc,
      bcc,
      subject,
      html,
      attachments,
    )
      .then(() => props.onClose(true))
      .catch(() => props.onClose(false))
      .finally(() => setIsLoading(false));
  };
  return (
    <form onSubmit={handleSubmit(processAndSendEmail)}>
      {isLoading && <LoadingSpinner />}
      {users != null && (
        <div className='d-flex flex-column gap-3 fw-semibold fs-5'>
          <EmailHeader
            cc={cc}
            bcc={bcc}
            replyToEmail={props.replyToEmail}
            users={users}
            emailTemplates={emailTemplates}
            emailTemplate={emailTemplate}
            setEmailTemplate={handleEmailTemplate}
            register={register}
            control={control}
            setCc={setCc}
            setBcc={setBcc}
            setSubjectText={setSubjectText}
          />
          <EmailRichTextEditor
            emailTemplate={emailTemplate}
            onChangeHtml={setHtml}
            control={control}
          />
          <EmailFooter
            attachments={attachments}
            setAttachments={setAttachments}
            formState={formState}
            onClose={props.onClose}
          />
        </div>
      )}
    </form>
  );
}
